import React, { useState } from 'react';
import { View, TouchableOpacity, Modal, FlatList, TouchableWithoutFeedback } from 'react-native';
import { Text } from '@shared/components/text';
import Icon from '@shared/components/md-design-system/Icon/Icon';
import { colorPalette } from '@shared/config/colorsConfig';

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  label: string;
  placeholder: string;
  value: string | null;
  options: SelectOption[];
  onChange: (value: string) => void;
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  placeholder,
  value,
  options,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <View className="mb-4">
      <Text className="text-base font-semibold text-gray-700 mb-1">{label}</Text>
      <TouchableOpacity
        className="flex-row items-center justify-between border border-gray-300 rounded-xl px-4 py-3 bg-white"
        activeOpacity={0.7}
        onPress={() => setIsOpen(true)}
      >
        <Text className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </Text>
        <Icon name="down" size={14} color={colorPalette.gray[500]} />
      </TouchableOpacity>

      <Modal visible={isOpen} transparent animationType="fade" onRequestClose={() => setIsOpen(false)}>
        <TouchableWithoutFeedback onPress={() => setIsOpen(false)}>
          <View className="absolute inset-0 bg-black/45" />
        </TouchableWithoutFeedback>
        <View className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pb-8">
          <View className="p-4 border-b border-gray-200">
            <Text className="text-base font-semibold text-gray-900">{label}</Text>
          </View>
          <FlatList
            data={options}
            keyExtractor={(item) => item.value}
            renderItem={({ item }) => (
              <TouchableOpacity
                className={`flex-row items-center justify-between px-4 py-4 ${item.value === value ? 'bg-primary-50' : ''}`}
                onPress={() => handleSelect(item.value)}
              >
                <Text className={`text-base ${item.value === value ? 'text-primary-700 font-semibold' : 'text-gray-900'}`}>
                  {item.label}
                </Text>
                {item.value === value && (
                  <Icon name="check" size={16} color={colorPalette.primary[700]} />
                )}
              </TouchableOpacity>
            )}
          />
        </View>
      </Modal>
    </View>
  );
};

export default SelectField;
